import { formatPercent, formatPrice } from '../lib/format';
import { snapshotToRow } from '../lib/marketMetrics';
import type { SymbolSnapshot } from '../types/market';

export function MarketPulse({ snapshots }: { snapshots: Record<string, SymbolSnapshot> }) {
  const rows = Object.values(snapshots).map(snapshotToRow).filter((row) => row.price != null);
  const analyzed = rows.filter((row) => row.signal);
  const buys = analyzed.filter((row) => row.signal === 'BUY').length;
  const sells = analyzed.filter((row) => row.signal === 'SELL').length;
  const holds = analyzed.filter((row) => row.signal === 'HOLD').length;
  const changes = rows.map((row) => row.changePct).filter((value): value is number => value != null);
  const avgChange = changes.length ? changes.reduce((a, b) => a + b, 0) / changes.length : undefined;
  const top = rows.filter((row) => row.changePct != null).sort((a, b) => Math.abs(b.changePct ?? 0) - Math.abs(a.changePct ?? 0))[0];
  const liveLatencies = rows.map((row) => row.latencyMs).filter((value): value is number => value != null && value > 0);
  const avgLatency = liveLatencies.length ? Math.round(liveLatencies.reduce((a, b) => a + b, 0) / liveLatencies.length) : 0;

  return (
    <section className="pulse-grid" aria-label="Market pulse">
      <PulseCard label="Watchlist breadth" value={`${buys} buy · ${sells} sell`} detail={`${holds} hold · ${analyzed.length} analyzed`} />
      <PulseCard label="Average move" value={formatPercent(avgChange)} detail={`${changes.length} symbols with price change`} tone={avgChange != null && avgChange < 0 ? 'negative' : 'positive'} />
      <PulseCard label="Largest mover" value={top ? `${top.symbol} ${formatPercent(top.changePct)}` : '—'} detail={top?.price != null ? formatPrice(top.price) : 'Waiting for data'} tone={(top?.changePct ?? 0) < 0 ? 'negative' : 'positive'} />
      <PulseCard label="Yahoo transport" value={avgLatency ? `${avgLatency} ms` : 'Cached / waiting'} detail="Average uncached bridge latency" />
    </section>
  );
}

function PulseCard({ label, value, detail, tone }: { label: string; value: string; detail: string; tone?: 'positive' | 'negative' }) {
  return (
    <article className="pulse-card">
      <span>{label}</span>
      <strong className={tone ?? ''}>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}
